import React, { useState, useEffect, useRef, createContext } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import axios from 'axios';
import { Toaster } from '@/components/ui/sonner';
import './App.css';

// Contexts
import { SubscriptionProvider } from './contexts/SubscriptionContext';

// Components
import { SubscriptionExpiredBanner } from './components/SubscriptionExpiredBanner';
import PassoAPasso from './components/PassoAPasso';

// Contexto para controlar o Passo a Passo
export const PassoAPassoContext = createContext(null);

// Pages (financial core only)
import LandingPage from './pages/LandingPage';
import Dashboard from './pages/Dashboard';
import Lancamentos from './pages/Lancamentos';
import ContasPagar from './pages/ContasPagar';
import NovoBoleto from './pages/NovoBoleto';
import ContasReceber from './pages/ContasReceber';
import Empresa from './pages/Empresa';
import MetaMensal from './pages/MetaMensal';
import { Clientes } from './pages/Clientes';
import PlanoContas from './pages/PlanoContas';
import Assinatura from './pages/Assinatura';
import AdminPanel from './pages/AdminPanel';
import AdminVideos from './pages/AdminVideos';
import AdminEducaEmpresario from './pages/AdminEducaEmpresario';
import Funcionarios from './pages/Funcionarios';
import Fornecedores from './pages/Fornecedores';
import Contadores from './pages/Contadores';
import ContadorDetalhe from './pages/ContadorDetalhe';
import NotasFiscais from './pages/NotasFiscais';
import ConferenciaMensal from './pages/ConferenciaMensal';
import NotasVenda from './pages/NotasVenda';
import NotasServico from './pages/NotasServico';
import NotasTomadas from './pages/NotasTomadas';
import OutrosDocumentos from './pages/OutrosDocumentos';
import PortalContador from './pages/PortalContador';
import DFCRelatorio from './pages/DFCRelatorio';
import DRE from './pages/DRE';
import CustosFixosRecorrentes from './pages/CustosFixosRecorrentes';
import CustosVariaveis from './pages/CustosVariaveis';
import ResetPassword from './pages/ResetPassword';
import EducaEmpresarioPage from './pages/EducaEmpresarioPage';
import AcessoLogin from './pages/AcessoLogin';

// Relatórios (financial only - 3 NICHO removed)
import Relatorios from './pages/Relatorios';
import RelContasPagar from './pages/relatorios/RelContasPagar';
import RelContasReceber from './pages/relatorios/RelContasReceber';
import RelAgingPagar from './pages/relatorios/RelAgingPagar';
import RelAgingReceber from './pages/relatorios/RelAgingReceber';
import RelFluxoProjetado from './pages/relatorios/RelFluxoProjetado';
import RelFluxoRealizado from './pages/relatorios/RelFluxoRealizado';
import RelDespesasCategoria from './pages/relatorios/RelDespesasCategoria';
import RelClientesRanking from './pages/relatorios/RelClientesRanking';
import RelFornecedoresRanking from './pages/relatorios/RelFornecedoresRanking';
import RelInadimplencia from './pages/relatorios/RelInadimplencia';
import RelDREGerencial from './pages/relatorios/RelDREGerencial';
import RelDFCCompleto from './pages/relatorios/RelDFCCompleto';
import RelClientesCadastro from './pages/relatorios/RelClientesCadastro';
import RelClientesRecorrencia from './pages/relatorios/RelClientesRecorrencia';
import RelClientesInadimplencia from './pages/relatorios/RelClientesInadimplencia';
import RelTopIndicadores from './pages/relatorios/RelTopIndicadores';
import RelAlertas from './pages/relatorios/RelAlertas';
import RelComparativo from './pages/relatorios/RelComparativo';
import RelPareto from './pages/relatorios/RelPareto';
import RelFormaPagamento from './pages/relatorios/RelFormaPagamento';

// Detectar URL do backend automaticamente
const getBackendUrl = () => {
  const hostname = window.location.hostname;
  if (hostname === 'localhost' || hostname === '127.0.0.1') {
    return 'http://localhost:8001';
  }
  if (hostname.includes('preview.emergentagent.com')) {
    return window.location.origin;
  }
  return process.env.REACT_APP_BACKEND_URL || window.location.origin;
};

const BACKEND_URL = getBackendUrl();
export const API = `${BACKEND_URL}/api`;

export const axiosInstance = axios.create({
  baseURL: API,
});

// ========== JWT interceptors (Phase 5) ==========
const TOKEN_KEY = 'segcore_access_token';
const REFRESH_KEY = 'segcore_refresh_token';

export const tokenStore = {
  getAccess: () => localStorage.getItem(TOKEN_KEY),
  getRefresh: () => localStorage.getItem(REFRESH_KEY),
  set: (access, refresh) => {
    if (access) localStorage.setItem(TOKEN_KEY, access);
    if (refresh) localStorage.setItem(REFRESH_KEY, refresh);
  },
  clear: () => {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(REFRESH_KEY);
  },
};

// Attach Bearer token on every request
axiosInstance.interceptors.request.use((config) => {
  const access = tokenStore.getAccess();
  if (access) {
    config.headers = config.headers || {};
    if (!config.headers.Authorization) {
      config.headers.Authorization = `Bearer ${access}`;
    }
  }
  return config;
});

// On 401, try refresh once; on failure -> hard logout
let isRefreshing = false;
let pendingQueue = [];
const processQueue = (error, token = null) => {
  pendingQueue.forEach(({ resolve, reject }) => {
    if (error) reject(error);
    else resolve(token);
  });
  pendingQueue = [];
};

axiosInstance.interceptors.response.use(
  (response) => response,
  async (error) => {
    const original = error.config;
    if (!error.response || error.response.status !== 401 || original._retry) {
      return Promise.reject(error);
    }
    // Don't try to refresh on /auth/login or /auth/refresh themselves
    if (original.url && (original.url.includes('/auth/login') || original.url.includes('/auth/refresh'))) {
      return Promise.reject(error);
    }
    const refresh = tokenStore.getRefresh();
    if (!refresh) {
      tokenStore.clear();
      localStorage.removeItem('user');
      window.location.href = '/';
      return Promise.reject(error);
    }
    original._retry = true;
    if (isRefreshing) {
      return new Promise((resolve, reject) => pendingQueue.push({ resolve, reject }))
        .then((newToken) => {
          original.headers.Authorization = `Bearer ${newToken}`;
          return axiosInstance(original);
        });
    }
    isRefreshing = true;
    try {
      const { data } = await axios.post(`${API}/auth/refresh`, { refresh_token: refresh });
      tokenStore.set(data.access_token, null);
      processQueue(null, data.access_token);
      original.headers.Authorization = `Bearer ${data.access_token}`;
      return axiosInstance(original);
    } catch (refreshErr) {
      processQueue(refreshErr, null);
      tokenStore.clear();
      localStorage.removeItem('user');
      window.location.href = '/';
      return Promise.reject(refreshErr);
    } finally {
      isRefreshing = false;
    }
  }
);

function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const passoAPassoRef = useRef(null);

  useEffect(() => {
    const storedUser = localStorage.getItem('user');
    if (storedUser) {
      setUser(JSON.parse(storedUser));
    }
    setLoading(false);
  }, []);

  const handleLogout = async () => {
    // Best-effort logout: revoke refresh token
    try {
      const refresh = localStorage.getItem('segcore_refresh_token');
      if (refresh) {
        await axiosInstance.post('/auth/logout', { refresh_token: refresh });
      }
    } catch (_e) { /* silent */ }
    localStorage.removeItem('user');
    localStorage.removeItem('company');
    localStorage.removeItem('permissoes');
    localStorage.removeItem('funcionario');
    localStorage.removeItem('segcore_access_token');
    localStorage.removeItem('segcore_refresh_token');
    setUser(null);
  };

  const openPassoAPasso = () => {
    if (passoAPassoRef.current) {
      passoAPassoRef.current.open();
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-xl text-white">Carregando...</div>
      </div>
    );
  }

  const GestorOnlyRoute = ({ children, permissao }) => {
    if (!user) return <Navigate to="/" replace />;
    const role = user.role;
    if (role === 'FUNCIONARIO' || role === 'funcionario') {
      const nivel = user.permissoes?.[permissao];
      if (permissao && (nivel === 'read' || nivel === 'write')) return children;
      return <Navigate to="/dashboard" replace />;
    }
    return children;
  };

  if (!user) {
    return (
      <BrowserRouter>
        <Toaster position="top-center" richColors />
        <Routes>
          <Route path="/" element={<LandingPage setUser={setUser} />} />
          <Route path="/acesso/:slug" element={<AcessoLogin setUser={setUser} />} />
          <Route path="/reset-password/:token" element={<ResetPassword />} />
          <Route path="/portal-contador/:token" element={<PortalContador />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
    );
  }

  return (
    <BrowserRouter>
      <Toaster position="top-center" richColors />
      <PassoAPassoContext.Provider value={{ openPassoAPasso }}>
        <SubscriptionProvider user={user}>
          <SubscriptionExpiredBanner />
          <Routes>
            <Route path="/" element={<Navigate to="/dashboard" replace />} />
            <Route path="/dashboard" element={<Dashboard user={user} onLogout={handleLogout} />} />
            <Route path="/portal-contador/:token" element={<PortalContador />} />

            {/* Financeiro - Gestor */}
            <Route path="/lancamentos" element={<GestorOnlyRoute permissao="lancamentos"><Lancamentos user={user} onLogout={handleLogout} /></GestorOnlyRoute>} />
            <Route path="/contas-pagar" element={<GestorOnlyRoute permissao="contas_pagar"><ContasPagar user={user} onLogout={handleLogout} /></GestorOnlyRoute>} />
            <Route path="/novo-boleto" element={<GestorOnlyRoute permissao="contas_pagar"><NovoBoleto user={user} onLogout={handleLogout} /></GestorOnlyRoute>} />
            <Route path="/contas-receber" element={<GestorOnlyRoute permissao="contas_receber"><ContasReceber user={user} onLogout={handleLogout} /></GestorOnlyRoute>} />
            <Route path="/custos-fixos" element={<GestorOnlyRoute><CustosFixosRecorrentes user={user} onLogout={handleLogout} /></GestorOnlyRoute>} />
            <Route path="/custos-variaveis" element={<GestorOnlyRoute><CustosVariaveis user={user} onLogout={handleLogout} /></GestorOnlyRoute>} />
            <Route path="/meta-mensal" element={<GestorOnlyRoute><MetaMensal user={user} onLogout={handleLogout} /></GestorOnlyRoute>} />
            <Route path="/plano-contas" element={<GestorOnlyRoute><PlanoContas user={user} onLogout={handleLogout} /></GestorOnlyRoute>} />
            <Route path="/dfc" element={<GestorOnlyRoute><DFCRelatorio user={user} onLogout={handleLogout} /></GestorOnlyRoute>} />
            <Route path="/dre" element={<GestorOnlyRoute><DRE user={user} onLogout={handleLogout} /></GestorOnlyRoute>} />

            {/* Cadastros - Gestor */}
            <Route path="/clientes" element={<GestorOnlyRoute permissao="clientes"><Clientes user={user} onLogout={handleLogout} /></GestorOnlyRoute>} />
            <Route path="/fornecedores" element={<GestorOnlyRoute permissao="fornecedores"><Fornecedores user={user} onLogout={handleLogout} /></GestorOnlyRoute>} />
            <Route path="/funcionarios" element={<GestorOnlyRoute><Funcionarios user={user} onLogout={handleLogout} /></GestorOnlyRoute>} />
            <Route path="/contadores" element={<GestorOnlyRoute><Contadores user={user} onLogout={handleLogout} /></GestorOnlyRoute>} />
            <Route path="/contadores/:id" element={<GestorOnlyRoute><ContadorDetalhe user={user} onLogout={handleLogout} /></GestorOnlyRoute>} />
            <Route path="/conferencia-mensal" element={<GestorOnlyRoute><ConferenciaMensal user={user} onLogout={handleLogout} /></GestorOnlyRoute>} />
            <Route path="/notas-fiscais" element={<GestorOnlyRoute><NotasFiscais user={user} onLogout={handleLogout} /></GestorOnlyRoute>} />
            <Route path="/notas-venda" element={<GestorOnlyRoute><NotasVenda user={user} onLogout={handleLogout} /></GestorOnlyRoute>} />
            <Route path="/notas-servico" element={<GestorOnlyRoute><NotasServico user={user} onLogout={handleLogout} /></GestorOnlyRoute>} />
            <Route path="/notas-tomadas" element={<GestorOnlyRoute><NotasTomadas user={user} onLogout={handleLogout} /></GestorOnlyRoute>} />
            <Route path="/outros-documentos" element={<GestorOnlyRoute><OutrosDocumentos user={user} onLogout={handleLogout} /></GestorOnlyRoute>} />

            {/* Geral */}
            <Route path="/empresa" element={<Empresa user={user} onLogout={handleLogout} />} />

            {/* Relatórios */}
            <Route path="/relatorios" element={<Relatorios user={user} onLogout={handleLogout} />} />
            <Route path="/relatorios/pagar-periodo" element={<GestorOnlyRoute><RelContasPagar user={user} onLogout={handleLogout} /></GestorOnlyRoute>} />
            <Route path="/relatorios/receber-periodo" element={<GestorOnlyRoute><RelContasReceber user={user} onLogout={handleLogout} /></GestorOnlyRoute>} />
            <Route path="/relatorios/pagar-aging" element={<GestorOnlyRoute><RelAgingPagar user={user} onLogout={handleLogout} /></GestorOnlyRoute>} />
            <Route path="/relatorios/receber-aging" element={<GestorOnlyRoute><RelAgingReceber user={user} onLogout={handleLogout} /></GestorOnlyRoute>} />
            <Route path="/relatorios/fluxo-projetado" element={<GestorOnlyRoute><RelFluxoProjetado user={user} onLogout={handleLogout} /></GestorOnlyRoute>} />
            <Route path="/relatorios/fluxo-realizado" element={<GestorOnlyRoute><RelFluxoRealizado user={user} onLogout={handleLogout} /></GestorOnlyRoute>} />
            <Route path="/relatorios/despesas-categoria" element={<GestorOnlyRoute><RelDespesasCategoria user={user} onLogout={handleLogout} /></GestorOnlyRoute>} />
            <Route path="/relatorios/clientes-ranking" element={<GestorOnlyRoute><RelClientesRanking user={user} onLogout={handleLogout} /></GestorOnlyRoute>} />
            <Route path="/relatorios/fornecedores-ranking" element={<GestorOnlyRoute><RelFornecedoresRanking user={user} onLogout={handleLogout} /></GestorOnlyRoute>} />
            <Route path="/relatorios/inadimplencia" element={<GestorOnlyRoute><RelInadimplencia user={user} onLogout={handleLogout} /></GestorOnlyRoute>} />
            <Route path="/relatorios/dre-gerencial" element={<RelDREGerencial user={user} onLogout={handleLogout} />} />
            <Route path="/relatorios/dfc-completo" element={<RelDFCCompleto user={user} onLogout={handleLogout} />} />
            <Route path="/relatorios/clientes-cadastro" element={<RelClientesCadastro user={user} onLogout={handleLogout} />} />
            <Route path="/relatorios/clientes-recorrencia" element={<RelClientesRecorrencia user={user} onLogout={handleLogout} />} />
            <Route path="/relatorios/clientes-inadimplencia" element={<RelClientesInadimplencia user={user} onLogout={handleLogout} />} />
            <Route path="/relatorios/top-indicadores" element={<RelTopIndicadores user={user} onLogout={handleLogout} />} />
            <Route path="/relatorios/alertas" element={<RelAlertas user={user} onLogout={handleLogout} />} />
            <Route path="/relatorios/comparativo" element={<RelComparativo user={user} onLogout={handleLogout} />} />
            <Route path="/relatorios/pareto" element={<RelPareto user={user} onLogout={handleLogout} />} />
            <Route path="/relatorios/forma-pagamento" element={<RelFormaPagamento user={user} onLogout={handleLogout} />} />

            {/* Assinatura / Educação */}
            <Route path="/assinatura" element={<GestorOnlyRoute><Assinatura user={user} onLogout={handleLogout} /></GestorOnlyRoute>} />
            <Route path="/educa-empresario" element={<EducaEmpresarioPage user={user} onLogout={handleLogout} />} />

            {/* Admin */}
            <Route path="/admin" element={user.role === 'ADMIN' || user.role === 'admin' ? <AdminPanel user={user} onLogout={handleLogout} /> : <Navigate to="/dashboard" replace />} />
            <Route path="/admin/videos" element={user.role === 'ADMIN' || user.role === 'admin' ? <AdminVideos user={user} onLogout={handleLogout} /> : <Navigate to="/dashboard" replace />} />
            <Route path="/admin/educa-empresario" element={user.role === 'ADMIN' || user.role === 'admin' ? <AdminEducaEmpresario user={user} onLogout={handleLogout} /> : <Navigate to="/dashboard" replace />} />

            <Route path="*" element={<Navigate to="/dashboard" replace />} />
          </Routes>
          <PassoAPasso ref={passoAPassoRef} />
        </SubscriptionProvider>
      </PassoAPassoContext.Provider>
    </BrowserRouter>
  );
}

export default App;
